import React from 'react';
import { X, AlertTriangle, AlertCircle, CheckCircle2, Edit2, Trash2, Copy, Send, PlusCircle, Lock } from 'lucide-react';

export default function ProposalScenariosList({
  proposalScenarios,
  editMode,
  selectedScenarios,
  onSelectAll,
  onSelectScenario,
  onEditScenario,
  onRemoveFromProposal,
  onDuplicateScenario,
  onSubmitSelected,
  onAddNewService,
  onBulkDelete  // NEW: Bulk delete handler
}) {
  const getApprovalBadge = (scenario) => {
    const badge = scenario.approval_badge;
    if (!badge) return null;

    const colorClasses = {
      red: 'bg-red-100 text-red-700 border-red-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300'
    };

    const IconComponent = badge.color === 'red' ? AlertTriangle : badge.color === 'orange' ? AlertCircle : CheckCircle2;

    return (
      <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded border ${colorClasses[badge.color] || colorClasses.blue}`}>
        <IconComponent className="w-3 h-3" />
        <span className="font-medium">{badge.text}</span>
      </div>
    );
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-xl font-bold text-slate-900">
            Calculations in This Proposal
          </h3>
          {editMode && proposalScenarios.length > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedScenarios.size === proposalScenarios.length && proposalScenarios.length > 0}
                onChange={onSelectAll}
                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-slate-700 font-medium">Select All</span>
            </label>
          )}
        </div>
        <p className="text-sm text-slate-600">
          {proposalScenarios.length} calculation{proposalScenarios.length !== 1 ? 's' : ''} added
          {editMode && selectedScenarios.size > 0 && (
            <span className="ml-2 text-indigo-600 font-semibold">
              ({selectedScenarios.size} selected)
            </span>
          )}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {proposalScenarios.map((scenario, index) => (
          <div 
            key={scenario.id} 
            className={`border rounded-lg p-4 bg-white transition-all ${
              editMode 
                ? 'cursor-pointer hover:shadow-lg hover:border-indigo-300' 
                : 'hover:shadow-md'
            } ${
              selectedScenarios.has(scenario.id) 
                ? 'border-indigo-500 bg-indigo-50 shadow-md' 
                : 'border-slate-200'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-start gap-2 flex-1">
                {editMode && (
                  <input
                    type="checkbox"
                    checked={selectedScenarios.has(scenario.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectScenario(scenario.id);
                    }}
                    className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                )}
                <div 
                  className="flex-1"
                  onClick={() => editMode && onEditScenario(scenario)}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                      #{index + 1}
                    </span>
                    <h4 className="font-semibold text-slate-900 text-sm">
                      {scenario.service_name || scenario.scenario_info?.service || 'Unknown Service'}
                    </h4>
                  </div>
                  
                  {/* Approval Badge */}
                  {scenario.approval_badge && (
                    <div className="mb-2">
                      {getApprovalBadge(scenario)}
                    </div>
                  )}
                  
                  {/* Reader Cost Recovery Badge */}
                  {scenario.reader_cost_recovery?.applied && (
                    <div className="mb-2">
                      <div className="flex items-center gap-1 text-xs px-2 py-1 rounded border bg-green-100 text-green-700 border-green-300">
                        <Lock className="w-3 h-3" />
                        <span className="font-medium">Reader Cost Recovery Applied</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 bg-slate-50 rounded p-2">
                        <div className="flex justify-between">
                          <span>Original Rate:</span>
                          <span className="line-through text-red-600">{scenario.reader_cost_recovery.original_rates?.CC?.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Adjusted Rate:</span>
                          <span className="font-semibold text-green-700">{scenario.reader_cost_recovery.adjusted_rates?.CC?.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between mt-1 pt-1 border-t border-slate-200">
                          <span className="font-medium">Rate Increase:</span>
                          <span className="font-bold text-green-600">
                            +{((scenario.reader_cost_recovery.adjusted_rates?.CC || 0) - (scenario.reader_cost_recovery.original_rates?.CC || 0)).toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-600 line-clamp-1">
                    {scenario.name || scenario.scenario_info?.scenarioName || 'Untitled Calculation'}
                  </p>
                </div>
              </div>
              {/* Edit, Duplicate, and Delete buttons - always visible */}
              <div className="flex gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditScenario(scenario);
                  }}
                  className="text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                  title="Edit calculation"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDuplicateScenario) {
                      onDuplicateScenario(scenario);
                    }
                  }}
                  className="text-green-500 hover:text-green-700 p-1 hover:bg-green-50 rounded"
                  title="Duplicate calculation"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFromProposal(scenario.id);
                  }}
                  className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                  title="Delete calculation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {scenario.profitability_results && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Gross Fees:</span>
                  <span className="font-semibold text-slate-900">
                    ${(scenario.profitability_results.totals?.total_fees || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Net Fees:</span>
                  <span className="font-semibold text-emerald-600">
                    ${(scenario.profitability_results.totals?.net_revenue || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Implied TR:</span>
                  <span className="font-semibold text-slate-900">
                    {(scenario.profitability_results.totals?.implied_tr || 0).toFixed(2)}%
                  </span>
                </div>
              </div>
            )}
            
            <div className="mt-2 text-xs text-slate-500">
              {scenario.scenario_info?.municipality && (
                <span>{scenario.scenario_info.municipality}, {scenario.scenario_info.state}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Action Bar - appears when scenarios are selected */}
      {selectedScenarios.size > 0 && (
        <div
          data-testid="proposal-action-bar"
          className="mt-6 p-5 rounded-xl bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 shadow-lg border border-indigo-500/30 animate-in slide-in-from-bottom-2"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">
                  {selectedScenarios.size} calculation{selectedScenarios.size !== 1 ? 's' : ''} selected
                </p>
                <p className="text-indigo-200 text-xs">
                  Choose an action below
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                data-testid="bulk-delete-btn"
                type="button"
                onClick={onBulkDelete}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-400/30 rounded-lg font-medium text-sm transition-all hover:shadow-md"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
              <button
                data-testid="add-new-service-btn"
                type="button"
                onClick={onAddNewService}
                className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg font-medium text-sm transition-all hover:shadow-md"
              >
                <PlusCircle className="w-4 h-4" />
                Add New Service
              </button>
              <button
                data-testid="submit-for-approval-btn"
                type="button"
                onClick={onSubmitSelected}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
              >
                <Send className="w-4 h-4" />
                Submit for Approval
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
